import React, { FunctionComponent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { LinearGradient } from 'expo-linear-gradient'
import styled, { useTheme } from 'styled-components/native'

import { PATHWAYS_TYPE } from '@lyrahealth-inc/shared-app-logic'

import {
  ArrowIcon,
  ArrowIconDirection,
  AuroraOrbIllustration,
  BodyText,
  Headline,
  PrimaryButton,
  Subhead,
  WordCloudButton,
} from '../../../atoms'
import { BodyTextSize, HeadlineSize, SubheadSize } from '../../../styles'
import { AuroraThemeType, tID } from '../../../utils'

export interface AuroraHeroSectionProps {
  pathwaysType?: PATHWAYS_TYPE
  onCreateAccountButtonPress?: () => void
  onProviderLedCardPress?: () => void
  onWellnessCheckinCardPress?: () => void
  onSelfCareCardPress?: () => void
  onNotifyMeOnLaunchPress?: () => void
  onExploreOnYourOwnCTAPress?: () => void
  selectedPillId?: string
  startDate?: string
  isAutoEnrollmentPathways?: boolean
}

const HeroContainer = styled(LinearGradient)<{ theme: AuroraThemeType }>(({ theme }) => ({
  width: '100%',
  minHeight: theme.breakpoints.isMobileSized ? '600px' : '500px',
  paddingTop: theme.breakpoints.isMobileSized ? theme.spacing['48px'] : theme.spacing['64px'],
  paddingBottom: theme.breakpoints.isMobileSized ? theme.spacing['48px'] : theme.spacing['64px'],
  paddingHorizontal: theme.spacing['16px'],
  alignItems: 'center',
}))

const ContentContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  maxWidth: theme.breakpoints.isMinWidthLaptop ? '800px' : '100%',
  alignItems: 'center',
  width: '100%',
  gap: theme.spacing['56px'],
}))

const IllustrationAndHeadlineContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing['24px'],
}))

const SubheadAndPillsContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing['32px'],
}))

const PillsContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  flexDirection: theme.breakpoints.isMobileSized ? 'column' : 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing['12px'],
}))

const ButtonContainer = styled.View({
  alignItems: 'center',
})

const ExploreContainer = styled.View<{ theme: AuroraThemeType }>(({ theme }) => ({
  alignItems: 'center',
  gap: theme.spacing['16px'],
}))

const PillWrapper = styled.View({})

const PILL_OPTIONS = [
  { id: 'therapy', labelKey: 'therapy' },
  { id: 'wellness', labelKey: 'wellness' },
  { id: 'selfCare', labelKey: 'selfCare' },
]

export const AuroraHeroSection: FunctionComponent<AuroraHeroSectionProps> = ({
  pathwaysType = PATHWAYS_TYPE.SINGLE,
  onCreateAccountButtonPress,
  onProviderLedCardPress,
  onWellnessCheckinCardPress,
  onSelfCareCardPress,
  onNotifyMeOnLaunchPress,
  onExploreOnYourOwnCTAPress,
  selectedPillId,
  startDate,
  isAutoEnrollmentPathways = false,
}) => {
  const pillPressHandlers: Record<string, (() => void) | undefined> = {
    therapy: onProviderLedCardPress,
    wellness: onWellnessCheckinCardPress,
    selfCare: onSelfCareCardPress,
  }

  const handlePillPress = (pillId: string) => {
    pillPressHandlers[pillId]?.()
  }
  const theme = useTheme() as AuroraThemeType
  const { breakpoints } = theme
  const isComingSoon = pathwaysType === PATHWAYS_TYPE.COMING_SOON
  const { formatDate } = useIntl()

  const headlineText = isComingSoon ? (
    <FormattedMessage defaultMessage='Get ready to feel your best' description='Coming soon hero section headline' />
  ) : (
    <FormattedMessage defaultMessage='Let’s help you feel your best' description='Hero section headline' />
  )

  const subheadText = isComingSoon ? (
    <FormattedMessage
      defaultMessage="We're putting the finishing touches on your new mental health benefits experience. It's designed to make finding the right care easy, flexible, and personalized to you."
      description='Coming soon subhead description'
    />
  ) : (
    <FormattedMessage
      defaultMessage='Choose one to get started'
      description='Subhead prompting user to select an option'
    />
  )

  const getPillLabel = (pillId: string) => {
    switch (pillId) {
      case 'therapy':
        return (
          <FormattedMessage
            defaultMessage='1-on-1 therapy and coaching'
            description='Therapy and coaching pill label'
          />
        )
      case 'wellness':
        return <FormattedMessage defaultMessage='Wellness check-in' description='Wellness check-in pill label' />
      case 'selfCare':
        return <FormattedMessage defaultMessage='Self care library' description='Self care library pill label' />
      default:
        return null
    }
  }

  const renderDefaultContent = () => (
    <>
      <SubheadAndPillsContainer>
        <Subhead
          size={breakpoints.isMobileSized ? SubheadSize.SMALL : SubheadSize.MEDIUM}
          text={subheadText}
          textAlign='center'
          color={theme.colors.textInverse}
        />
        <PillsContainer>
          {PILL_OPTIONS.map(({ id }) => (
            <PillWrapper key={id}>
              <WordCloudButton
                text={getPillLabel(id)}
                onPress={() => handlePillPress(id)}
                isSelected={selectedPillId === id}
                testID={tID(`AuroraHeroSection-pill-${id}`)}
              />
            </PillWrapper>
          ))}
        </PillsContainer>
      </SubheadAndPillsContainer>
      <ButtonContainer>
        <PrimaryButton
          text={
            isAutoEnrollmentPathways ? (
              <FormattedMessage
                defaultMessage='Understand your coverage'
                description='CTA button text for auto enrollment users to understand their coverage'
              />
            ) : (
              <FormattedMessage defaultMessage='Sign up and explore' description='Primary CTA button text' />
            )
          }
          onPress={() => onCreateAccountButtonPress?.()}
          testID={tID('HeroSection-registrationButton')}
        />
      </ButtonContainer>
    </>
  )

  const renderComingSoonContent = () => (
    <>
      <BodyText size={BodyTextSize.DEFAULT} text={subheadText} textAlign='center' color={theme.colors.textInverse} />
      <ButtonContainer>
        <PrimaryButton
          text={<FormattedMessage defaultMessage='Notify me on launch' description='Notify me CTA button text' />}
          onPress={() => onNotifyMeOnLaunchPress?.()}
          testID={tID('HeroSection-notifyMeOnLaunchButton')}
        />
      </ButtonContainer>
      <ExploreContainer>
        <BodyText
          size={BodyTextSize.LARGE}
          text={<FormattedMessage defaultMessage='Explore on your own' description='Explore on your own CTA' />}
          textAlign='center'
          color={theme.colors.textInverse}
          onPress={() => onExploreOnYourOwnCTAPress?.()}
          testID={tID('HeroSection-exploreOnYourOwnCTA')}
        />
        <ArrowIcon size={24} fillColor={theme.colors.textInverse} direction={ArrowIconDirection.DOWN} />
      </ExploreContainer>
    </>
  )

  return (
    <HeroContainer
      colors={theme.colors.components.heroSection.background.gradient}
      start={{ x: 0.5, y: 0 }}
      end={{ x: 0.5, y: 1 }}
      testID={tID('HeroSection-pathwaysHomepage')}
    >
      <ContentContainer>
        <IllustrationAndHeadlineContainer>
          <AuroraOrbIllustration size={breakpoints.isMobileSized ? 200 : 284} />
          {isComingSoon && startDate && (
            <Subhead
              size={SubheadSize.MEDIUM}
              text={
                <FormattedMessage
                  defaultMessage='Launching {startDate}'
                  description='Subhead text indicating when date customer launches'
                  values={{
                    startDate: formatDate(startDate, {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      timeZone: 'UTC',
                    }),
                  }}
                />
              }
              textAlign='center'
              color={theme.colors.textInverse}
            />
          )}
          <Headline
            size={breakpoints.isMobileSized ? HeadlineSize.SMALL : HeadlineSize.MEDIUM}
            text={headlineText}
            textAlign='center'
            color={theme.colors.textInverse}
          />
        </IllustrationAndHeadlineContainer>
        {isComingSoon ? renderComingSoonContent() : renderDefaultContent()}
      </ContentContainer>
    </HeroContainer>
  )
}
